<template>
	<VDialogV3
		id="shipment-requisites"
		position="right"
		width="600px"
		hide-overlay-blur
    :entity="localShipment"
		@submit="onSubmit"
	>
			<section>
				<VSwitch
					v-model="localShipment.isShipmentByAssembly"
					theme="slider"
					checked-label="Отгрузка по сборочным"
					unchecked-label="Отгрузка без сборочного"
					:disabled="!isInWork || !isNewDocument"
				/>
			</section>

			<hr>

			<section>
				<h1>Основное</h1>

				<VSelectV2
					v-model="localShipment.seller"
					label="Поставщик"
					layout="vertical"
					required
					:disabled="!isNewDocument || localShipment.isReceived"
					:data="sellerSource"
				/>

				<VSelectV2
					v-model="localShipment.sellerCompany"
					label="Юр. лицо"
					layout="vertical"
					hide-clear-btn
					:data="sellerCompanySource"
				/>

				<VSelectV2
					v-model="localShipment.sellerStore"
					label="Склад отправки"
					layout="vertical"
					hide-clear-btn
					:disabled="localShipment.isTransportOrderCreated || !sellerStoreHasTransportZone"
					:data="sellerStoreSource"
				/>

				<!-- участок прихода временно скрыт, задача будет обновлена
				<VSwitch
					v-if="$core.currentPartner.isLSZ"
					:model-value="receptionAreaOptionsSelectVisible"
					theme="slider"
					checked-label="Указать участок прихода для немолотого сырья"
					unchecked-label="Не указывать участок прихода для немолотого сырья"
					@update:modelValue="changeReceptionAreaOptionsSelectVisibility"
				/>

				<VSelect
					v-if="receptionAreaOptionsSelectVisible && $core.currentPartner.isLSZ"
					v-model="localShipment.receptionArea"
					class="reception-area"
					label="Участок прихода"
					placeholder="Выберите участок прихода"
					required
					layout="vertical"
					:options="receptionAreaOptions"
				/>
				-->
			</section>

			<section>
				<!-- скрывает покупатель-склад от seller при создании отгрузки -->
				<template v-if="isSeller">
					<VSelectV2
						v-model="localShipment.buyerCompany"
						disabled
						label="Юр. лицо покупателя"
						layout="vertical"
					/>
				</template>
				<template v-else>
					<VSelectV2
						v-model="localShipment.buyer"
						label="Покупатель"
						layout="vertical"
						required
						disabled
					/>

					<VSelectV2
						v-model="localShipment.buyerCompany"
						label="Юр. лицо"
						layout="vertical"
						hide-clear-btn
						:disabled="!isNewDocument"
						:data="buyerCompanySource"
					/>
				</template>
			</section>

			<section>
				<VDateInput
					v-model="localShipment.shipmentDate"
					label="Дата отгрузки"
					layout="vertical"
					required
					:disabled="localShipment.isReceived"
				/>
			</section>

			<hr>

			<section>
				<h1>Документ</h1>

				<VSelect
					v-model="localShipment.documentType"
					label="Тип документа"
					layout="vertical"
					required
					:disabled="localShipment.isReceived"
					:options="documentTypeList"
				/>

				<VInput
					v-model="localShipment.documentNumber"
					label="Номер документа"
					layout="vertical"
					required
					:max-length="300"
					:validate-method="validateDocumentNumber"
				/>

				<VDateInput
					v-model="localShipment.documentDate"
					label="Дата документа"
					layout="vertical"
					required
					:validate-method="validateDocumentDate"
				/>

				<VSwitch
					v-model="localShipment.isCurrencyRateBasedAccountingCurrencyRateDate"
					theme="slider"
					checked-label="Курс на другую дату"
					unchecked-label="Курс на основании даты документа"
				/>

				<VDateInput
					v-if="localShipment.isCurrencyRateBasedAccountingCurrencyRateDate"
					v-model="localShipment.accountingCurrencyRateDate"
					label="Дата курса"
					layout="vertical"
					:required="localShipment.isCurrencyRateBasedAccountingCurrencyRateDate"
					:validate-method="validateAccountingCurrencyRateDate"
				/>
			</section>

			<section :class="{ 'disabled-overlay': localShipment.positions.length === 0 }">
				<VInput
					label="Валюта сделки"
					layout="vertical"
					disabled
					:value="localShipment.orderCurrency?.code"
				/>

				<VInput
					label="Валюта учёта"
					layout="vertical"
					required
					disabled
					:value="localShipment.accountingCurrencyCode"
				/>

        <VInputNumberRusV2
          v-model="localShipment.accountingCurrencyRate"
          :type="CurrencyRate"
          label="Курс"
          layout="vertical"
          disabled
        >
          <template #helper-text>
            <!-- без позиций курс не рассчитывается, ошибку показываем только при наличии позиций -->
            <div
              v-if="localShipment.positions.length > 0 && $core.utils.isNullOrUndefined(localShipment.accountingCurrencyRate) === true"
              class="error"
            >
              Курс не загружен, расчёт документа невозможен
            </div>
            <div
              v-else-if="localShipment.currencyRateDocumentDate !== null && isLimitExceeded === true"
              class="error"
            >
              Курс сильно отличается от курса ЦБ РФ на дату документа (курс на дату:
              {{ localShipment.currencyRateDocumentDate.format() }})
            </div>
          </template>
        </VInputNumberRusV2>

        <VInputNumberRusV2
          :model-value="localShipment.accountingSumFull ?? localShipment.accountingSumFullCalculated"
          @input="modelValue => (localShipment.accountingSumFull = modelValue)"
          :type="Sum"
          label="Сумма полная"
          layout="vertical"
          required
          :class="{ 'touched-input': localShipment.accountingSumFull?.isPositive() === true }"
          :disabled="isFreeSamples"
          :validate-method="validateAccountingSumFull"
          :unit="$core.utils.money.getSymbolByCurrency(localShipment.accountingCurrencyCode)"
        />

				<VSwitch
					v-if="localShipment.sellerCompany?.isVatPayer"
					disabled
					theme="slider"
					label="НДС"
					layout="vertical"
					checked-label="Включительно"
					unchecked-label="Сверху"
					v-model="localShipment.isVatIncluded"
				/>

				<VInput
					v-else
					label="НДС"
					layout="vertical"
					disabled
					value="Не облагается"
				/>

        <VInputNumberRusV2
          :model-value="localShipment.accountingVatSum ?? localShipment.accountingVatSumCalculated"
          @input="modelValue => (localShipment.accountingVatSum = modelValue)"
          :type="Sum"
          label="Сумма НДС"
          layout="vertical"
          :class="{ 'touched-input': localShipment.accountingVatSum?.isPositive() === true }"
          :disabled="isFreeSamples || localShipment.sellerCompany?.isVatPayer !== true"
          :validate-method="validateAccountingVatSum"
          :unit="$core.utils.money.getSymbolByCurrency(localShipment.accountingCurrencyCode)"
        />
			</section>

			<section v-if="$core.currentPartner.isBuyer">
				<VSwitch
					v-model="localShipment.isVerified"
					theme="slider"
					checked-label="Документ сверен"
					unchecked-label="Документ не сверен"
				/>
			</section>

			<hr>

			<section>
				<h1>Условия доставки</h1>

				<VSelectV2
					v-model="localShipment.deliveryType"
					label="Тип доставки"
					layout="vertical"
					disabled
				/>

				<VSelectV2
					v-model="localShipment.buyerStore"
					label="Склад доставки"
					layout="vertical"
					:data="buyerStoreSource"
				/>
			</section>

			<section class="delivery-info">
				<header>Пункт отгрузки</header>
				<template v-if="localShipment.sellerStore">
					<div class="text-wrap" :title="localShipment.sellerStore.address">
						{{ localShipment.sellerStore.address || '---' }}
					</div>
					<footer v-if="localShipment.sellerStore.workingTime">
						Режим работы: {{ localShipment.sellerStore.workingTime }}
					</footer>
				</template>
				<template v-else>--</template>
			</section>

			<section class="delivery-info">
				<header>Пункт доставки</header>
				<template v-if="localShipment.buyerStore">
					<div class="text-wrap" :title="localShipment.buyerStore.address">
						{{ localShipment.buyerStore.address || '---' }}
					</div>
					<footer v-if="localShipment.buyerStore.workingTime">
						Режим работы: {{ localShipment.buyerStore.workingTime }}
					</footer>
					<footer v-if="localShipment.buyerStore.notes">
						Особые требования: {{ localShipment.buyerStore.notes }}
					</footer>
				</template>
				<template v-else>--</template>
			</section>

<!--			<section v-if="availableArrivalDate">-->
<!--				<VInput-->
<!--					v-model="localShipment.delivererName"-->
<!--					label="Наименование ТК"-->
<!--					layout="vertical"-->
<!--					:max-length="100"-->
<!--				/>-->

<!--				<VInput-->
<!--					v-model="localShipment.trackingNumber"-->
<!--					label="Трек-номер"-->
<!--					layout="vertical"-->
<!--					:max-length="100"-->
<!--				/>-->

<!--				<VDateInput-->
<!--					v-model="localShipment.arrivalDatePlanned"-->
<!--					label="Дата прибытия"-->
<!--					layout="vertical"-->
<!--					clearable-->
<!--				/>-->
<!--			</section>-->

			<hr>

			<section>
				<VSelectV2
					v-model="responsible"
					required
					label="Ответственный"
					placeholder="Введите ФИО ответственного"
					layout="vertical"
					class="responsible-input"
					:data="responsibleSource"
				/>
			</section>

			<section>
				<VInput
					label="Категория заказа"
					disabled
					layout="vertical"
					:value="orderCategory"
				/>

				<VSwitch
					v-if="typeof localShipment.isOfferBased === 'boolean'"
					v-model="localShipment.isOfferBased"
					theme="slider"
					disabled
					checked-label="Создан на основе предложения"
					unchecked-label="Создан не на основе предложения"
					layout="vertical"
				/>
			</section>
	</VDialogV3>
</template>

<script>

import CurrenciesService from '@/domains/Currencies/CurrenciesService';
import StoresService     from '@/domains/Stores/StoresService';
import Shipment          from '@/domains/Shipments/Shipment';
import PartnersService   from '@/domains/Partners/PartnersService';
import CompaniesService  from '@/domains/Companies/CompaniesService';
import CurrencyRate      from "@/core/types/CurrencyRate";
import Sum               from '@/core/types/Sum';

// участок прихода временно скрыт, задача будет обновлена
// const receptionAreaOptions = [
// 	{
// 		id:   'berdsk',
// 		name: 'Бердск. Участок приема сырья',
// 	},
// 	{
// 		id:   'btp',
// 		name: 'БТП. Участок приема сырья',
// 	},
// ];

export default defineComponent({
	name: 'ShipmentRequisites',

	props: {
		isInWork: {
			type:    Boolean,
			default: true,
		},
		shipment: {
			type:    Object,
			default: null,
		},
	},

	emits: ['updated'],

  setup() {
    return {
      CurrencyRate,
      Sum,
    }
  },

	data() {
		const isNewDocument = this.$core.utils.isNullOrUndefined(this.shipment?.id);

		return {
			isNewDocument,

			localShipment:    this.$core.utils.cloneDeep(this.shipment),
			documentTypeList: Shipment.DOCUMENT_TYPE_LIST,

			// участок прихода временно скрыт, задача будет обновлена
			// receptionAreaOptionsSelectVisible: this.shipment?.receptionArea !== null,
			// receptionAreaOptions:              receptionAreaOptions,

			sellerSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

			sellerCompanySource: this.$core.loader.select(({ seller }) => CompaniesService.getList({ partnerId: seller.id }))
				.dependsOn({ seller: () => this.localShipment.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

      /** Предыдущее ограничение по транспортной зоне было снято */
      sellerStoreSource: this.$core.loader.select(({ seller }) => StoresService.getList({
					partnerId:       seller.id,
					//transportZoneId: this.shipment?.sellerStore?.transportZone?.id,
				}))
				.dependsOn({ seller: () => this.localShipment.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			buyerCompanySource: this.$core.loader.select(({ buyer }) => buyer.companies ?? [])
				.dependsOn({ buyer: () => this.localShipment.buyer })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

      buyerStoreSource: this.$core.loader.select(({ buyer }) => StoresService.getList({
          partnerId:       buyer.id,
        }))
        .dependsOn({ buyer: () => this.localShipment.buyer })
        .preloadOnMount(isNewDocument === true)
        .preloadOnFocus(isNewDocument === false),

			responsibleSource: this.$core.loader.responsible(),
		};
	},

	computed: {
		isSeller() {
			return this.$core.currentPartner.isSeller;
		},

		isFreeSamples() {
			return this.localShipment.orderCategory?.id === 'freeSamples';
		},

    /*
		availableArrivalDate() {
			if (this.localShipment.deliveryType) {
				return this.localShipment.deliveryType.id !== 'bySeller';
			}
			return false;
		},
     */

    isLimitExceeded() {
      if (this.$core.utils.isNullOrUndefined(this.localShipment.currencyRateDocumentDate)) {
        return false;
      }
      else if (this.$core.utils.isNullOrUndefined(this.localShipment.accountingCurrencyRate)) {
        return false;
      }
      else {
        return !this.$core.utils.number.isCloseTo(
          this.localShipment.currencyRateDocumentDate.toNumber(),
          this.localShipment.accountingCurrencyRate.toNumber(),
          0.01,
        );
      }
    },

		orderCategory() {
			return this.localShipment?.orderCategory?.name ?? null;
		},

		sellerStoreHasTransportZone() {
			return this.localShipment.sellerStore?.transportZone?.id != null;
		},

		responsible: {
			get() {
				if (this.isSeller) {
					return this.localShipment.responsibleSeller;
				}
				return this.localShipment.responsibleBuyer;
			},
			set(val) {
				if (this.isSeller) {
					this.localShipment.responsibleSeller = val;
				} else {
					this.localShipment.responsibleBuyer = val;
				}
			},
		},

	},

  created() {
    if (!this.localShipment.currencyRateDocumentDate && this.isNewDocument === false) {
      CurrenciesService.getRate(
        this.localShipment.orderCurrencyCode,
        this.$core.acl.currentPartnerCurrencyCode,
        this.localShipment.documentDate,
      ).then(rate => {
        this.localShipment.currencyRateDocumentDate = rate;
      });
    }
  },

	methods: {

		async onSubmit() {
			this.$emit('updated', this.localShipment);
			this.$core.dialogs.ShipmentRequisites.hide();
		},

    validateAccountingSumFull() {
      if (this.$core.utils.isNullOrUndefined(this.localShipment.accountingSumFull)) { return null; }

      if (this.$core.utils.isNullOrUndefined(this.localShipment.accountingSumFullCalculated)) { return null; }

      if (this.localShipment.accountingSumFullCalculated.isZero()) { return null; }

      if (!this.$core.utils.number.isCloseTo(
        this.localShipment.accountingSumFullCalculated.toNumber(),
        this.localShipment.accountingSumFull.toNumber(),
      )) {
        return `Отклонение от ${this.localShipment.accountingSumFullCalculated.format(this.localShipment.accountingCurrencyCode)} более чем на 1%`;
      }
      return null;
    },

    validateAccountingVatSum() {
      if (this.$core.utils.isNullOrUndefined(this.localShipment.accountingVatSum)) { return null; }

      if (this.$core.utils.isNullOrUndefined(this.localShipment.accountingVatSumCalculated)) { return null; }

      if (this.localShipment.accountingVatSumCalculated.isZero()) { return null; }

      if (!this.$core.utils.number.isCloseTo(
        this.localShipment.accountingVatSumCalculated.toNumber(),
        this.localShipment.accountingVatSum.toNumber(),
      )) {
        return `Отклонение от ${this.localShipment.accountingVatSumCalculated.format(this.localShipment.accountingCurrencyCode)} более чем на 1%`;
      }
      return null;
    },

		validateDocumentDate(value) {
			if (!(value instanceof Date)) { return null; }
			const diff = (this.$core.utils.date.toDayEnd(value) - this.$core.utils.date.toDayEnd(new Date())) / 86_400_000;
			if (diff < -365) {
				return 'Нельзя указать больше 1 года назад';
			}

      if (this.localShipment.isCurrencyRateBasedAccountingCurrencyRateDate === false && value > new Date()) {
        return 'Нельзя выбрать дату в будущем, при выбранном "Курс на основании даты документа"';
      }

			return null;
		},

		validateAccountingCurrencyRateDate(value) {
      if (value > new Date()) {
        return 'Нельзя выбрать дату в будущем, так как курса на нее не существует';
      }

			return this.validateDocumentDate(value);
		},

		validateDocumentNumber(value) {
			if (!(typeof value === 'string' && value.length > 0)) { return null; }
			return /(,|;|:)/gm.test(value) ? 'Знаки , : ; запрещены' : null;
		},

		manualSetAccountingSum(value) {
			this.localShipment.accountingSumFull = Number(value);
		},

		manualSetAccountingVatSum(value) {
			this.localShipment.accountingVatSum = Number(value);
		},

		// участок прихода временно скрыт, задача будет обновлена
		// changeReceptionAreaOptionsSelectVisibility(val) {
		// 	this.receptionAreaOptionsSelectVisible = val;
		// 	if (!val) {
		// 		this.localShipment.receptionArea = null;
		// 	}
		// },
	},
});
</script>
